import { Injectable } from '@angular/core';
import { ReportCloneClientService } from '@mode/reports/data-access';
import { ReportCloneProgressComponent, ReportCloneSuccessComponent } from '@mode/reports/util';
import { SocketService } from '@mode/shared/data-access-socket';
import { ToastService } from '@mode/shared/util-toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SocketSubscription } from '@mode/shared/data-access-socket';
import { catchError, filter, map, of, switchMap, take, tap, mergeMap } from 'rxjs';
import * as ReportCloneActions from './report-clone.actions';
import { ReportCloneEvents, ReportCloneSuccessPayload } from './report-clone.models';
import { progressToastId } from './report-clone.selectors';
import { ReportClientService } from '@mode/shared/data-access-webapp';

@Injectable()
export class ReportCloneEffects {
  initReportClone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportCloneActions.initReportClone),
      mergeMap(({ ownerId, reportId, runId, reportWorkspace }) =>
        this.reportCloneClientService.initiateReportClone(ownerId, reportId, runId, reportWorkspace).pipe(
          map((response) =>
            ReportCloneActions.initReportCloneSuccess({
              reportId,
              channelName: response.pusher_channel_name,
            })
          ),
          catchError((error) => of(ReportCloneActions.initReportCloneFailure({ reportId, error })))
        )
      )
    )
  );

  initReportCloneFailure = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportCloneActions.initReportCloneFailure),
      map(({ reportId }) => ReportCloneActions.reportCloneFailed({ reportId }))
    )
  );

  initReportCloneSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportCloneActions.initReportCloneSuccess),
      map(({ reportId, channelName }) => {
        let reportCloneSubscription = {} as SocketSubscription<ReportCloneSuccessPayload>;
        try {
          reportCloneSubscription = this.socketService.connect<ReportCloneSuccessPayload>(channelName);
        } catch (error: any) {
          error.reportId = reportId;
          throw error;
        }
        return ReportCloneActions.reportCloneSocketConnectionSuccess({ reportId, reportCloneSubscription });
      }),
      catchError(({ reportId }) => of(ReportCloneActions.reportCloneFailed({ reportId })))
    )
  );

  reportCloneSocketConnectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportCloneActions.reportCloneSocketConnectionSuccess),
      mergeMap(({ reportId, reportCloneSubscription }) =>
        reportCloneSubscription.eventBus.pipe(
          filter((event) => Object.values(ReportCloneEvents).includes(event.name as ReportCloneEvents)),
          map((event) => {
            switch (event.name) {
              case ReportCloneEvents.DuplicateCompleted:
                reportCloneSubscription.disconnect();
                return ReportCloneActions.reportCloneSuccess({
                  reportId,
                  clonedReportApiUrl: event.data._links.report_duplicate.href,
                  clonedReportLink: event.data._links.web_edit_report_duplicate.href,
                });
              case ReportCloneEvents.DuplicateFailed:
                reportCloneSubscription.disconnect();
                return ReportCloneActions.reportCloneFailed({ reportId });
            }

            return ReportCloneActions.reportClonePending({ reportId });
          })
        )
      )
    )
  );

  loadClonedReportData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportCloneActions.reportCloneSuccess),
      mergeMap(({ clonedReportApiUrl }) =>
        this.reportClientService
          .getClonedReportData(clonedReportApiUrl)
          .pipe(map((clonedReport) => ReportCloneActions.clonedReportLoaded({ clonedReport })))
      )
    )
  );

  reportCloneSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportCloneActions.reportCloneSuccess),
        mergeMap(({ reportId, clonedReportLink }) =>
          this.store.select(progressToastId(reportId)).pipe(
            take(1),
            tap((toastId) => {
              if (toastId) {
                this.toastService.remove(toastId);
              }
              this.toastService.success('', {
                closeButton: true,
                disableTimeout: true,
                tapToDismiss: false,
                toastComponent: ReportCloneSuccessComponent,
                payload: { clonedReportLink },
              });
            })
          )
        )
      ),
    { dispatch: false }
  );

  reportCloneFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportCloneActions.reportCloneFailed),
        switchMap(({ reportId }) => this.store.select(progressToastId(reportId)).pipe(take(1))),
        tap((toastId) => {
          if (toastId) {
            this.toastService.remove(toastId);
          }
          this.toastService.error('Report duplication failed. Please try again.');
        })
      ),
    { dispatch: false }
  );

  reportClonePending$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportCloneActions.initReportClone),
      map(({ reportId }) => ({
        reportId,
        activeToast: this.toastService.info<unknown, ReportCloneProgressComponent>('', {
          disableTimeout: true,
          tapToDismiss: false,
          toastComponent: ReportCloneProgressComponent,
        }),
      })),
      map(({ reportId, activeToast }) =>
        ReportCloneActions.reportCloneInProgress({ reportId, progressToastId: activeToast.toastId })
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private reportCloneClientService: ReportCloneClientService,
    private reportClientService: ReportClientService,
    private socketService: SocketService,
    private toastService: ToastService,
    private store: Store
  ) {}
}
